/**
 * Map Section Switch
 * 
 * Control: Current implementation (includes charts, schools, demographics bundled together)
 * Variation: Clean map-only section with optimized loading
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { Box } from '@mui/material';
import { InView } from 'react-intersection-observer';
import { AllowedExperimentKey } from '@tenants/types';
import { LOCALE_LISTING_PATHS, Namespaces } from 'types/locales';
import { ExperimentalSection } from 'ui/experimental-section';
import { ContentSectionTemplate } from 'components/atomic/templates/content-section';
import Map, { TrackEventLocation } from 'components/map/listing';
import LocationUnavailable from 'components/pages/address/public/components/location-unavailable';
import { useCurrentBreakpointV2 } from 'hooks/use-breakpoint-v2';
import type { GeoJsonPoint } from '@zoocasa/node-kit/map/types';

// ============================================================================
// Types
// ============================================================================

export interface MapSectionProps {
  /** Section ID for scroll targeting */
  id: string;
  /** Listing ID */
  listingId: number;
  /** Listing position coordinates */
  position?: GeoJsonPoint;
  /** Full address string */
  fullAddress: string;
  /** Whether map is interactive */
  isInteractive: boolean;
  /** Location unavailable props */
  locationUnavailableProps?: { src?: string; message?: string };
  /** Map container class name */
  mapClassName?: string;
}

// ============================================================================
// Shared Content Component
// ============================================================================

type MapContentProps = Omit<MapSectionProps, 'id'> & {
  /** Whether to use optimized loading (for sticky tabs variation) */
  useOptimizedLoading?: boolean;
};

/**
 * Map content - renders only the map itself.
 * Charts, schools, and demographics are separate sections.
 */
function MapContent({
  listingId,
  position,
  fullAddress,
  isInteractive,
  locationUnavailableProps,
  mapClassName,
  useOptimizedLoading = false,
}: MapContentProps) {
  const hasAddressCoordinates = position?.coordinates?.length === 2;
  const canShowMap = hasAddressCoordinates && position;

  if (!canShowMap) {
    return <LocationUnavailable {...locationUnavailableProps} />;
  }

  if (useOptimizedLoading) {
    return (
      <OptimizedMapLoader
        listingId={listingId}
        position={position}
        fullAddress={fullAddress}
        isInteractive={isInteractive}
        mapClassName={mapClassName}
      />
    );
  }

  return (
    <Map
      listingId={`${listingId}-section`}
      position={position}
      address={fullAddress}
      className={mapClassName}
      isInteractive={isInteractive}
      loadWhenVisible
      trackEventLocation={TrackEventLocation.Page}
    />
  );
}

// ============================================================================
// Optimized Map Loader (Sticky Tabs Only)
// ============================================================================

interface OptimizedMapLoaderProps {
  listingId: number;
  position: GeoJsonPoint;
  fullAddress: string;
  isInteractive: boolean;
  mapClassName?: string;
}

/**
 * Responsive root margins for optimized map loading.
 * 
 * Strategy:
 * - Mobile: 400px (~0.5 viewport) - slower scrolling, smaller screen
 * - Tablet: 600px (~0.6 viewport) - medium scrolling speed
 * - Desktop: 800px (~0.7 viewport) - faster scrolling, larger screen
 * - Large Desktop: 1000px (~0.7 viewport) - fastest scrolling
 * 
 * These values ensure the map starts loading ~0.5-0.7 viewports before
 * it becomes visible, giving it time to render smoothly.
 */
const ROOT_MARGIN_BY_BREAKPOINT = {
  mobile: '400px',
  tablet: '600px',
  desktop: '800px',
  'large-desktop': '1000px',
} as const;

/**
 * Minimum height to reserve space and prevent layout shift.
 * Responsive to match typical map heights at each breakpoint.
 */
const MIN_HEIGHT_BY_BREAKPOINT = {
  mobile: '300px',
  tablet: '400px',
  desktop: '450px',
  'large-desktop': '500px',
} as const;

/**
 * Optimized map loader for sticky tabs variation.
 * 
 * Loads map before it enters viewport (responsive distance) to prevent 
 * layout shift and janky loading when scrolling from tab clicks.
 * 
 * Uses responsive root margins based on viewport size:
 * - Mobile: 400px (~0.5 viewport)
 * - Tablet: 600px (~0.6 viewport)
 * - Desktop: 800px (~0.7 viewport)
 * - Large Desktop: 1000px (~0.7 viewport)
 */
function OptimizedMapLoader({
  listingId,
  position,
  fullAddress,
  isInteractive,
  mapClassName,
}: OptimizedMapLoaderProps) {
  const [shouldLoad, setShouldLoad] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const breakpoint = useCurrentBreakpointV2();

  // Get responsive values based on current breakpoint
  const rootMargin = useMemo(() => ROOT_MARGIN_BY_BREAKPOINT[breakpoint], [breakpoint]);
  const minHeight = useMemo(() => MIN_HEIGHT_BY_BREAKPOINT[breakpoint], [breakpoint]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setShouldRender(true);
      if (!window.IntersectionObserver) {
        import('intersection-observer');
      }
    }
  }, []);

  const handleVisibilityChange = useCallback((inView: boolean) => {
    if (inView && !shouldLoad) {
      setShouldLoad(true);
    }
  }, [shouldLoad]);

  if (!shouldRender) return null;

  return (
    <InView onChange={handleVisibilityChange} rootMargin={rootMargin} triggerOnce>
      <div style={{ minHeight: shouldLoad ? undefined : minHeight }}>
        <Map
          listingId={`${listingId}-section`}
          position={position}
          address={fullAddress}
          className={mapClassName}
          isInteractive={isInteractive}
          loadWhenVisible={false}
          trackEventLocation={TrackEventLocation.Page}
        />
      </div>
    </InView>
  );
}

// ============================================================================
// Control (Current Implementation)
// ============================================================================

function MapSectionControl({ id, ...contentProps }: MapSectionProps) {
  return (
    <Box component="section" id={id} sx={{ scrollMarginTop: 'var(--scroll-offset, 150px)' }}>
      <MapContent {...contentProps} />
    </Box>
  );
}

// ============================================================================
// Variation (New Design with Templates + Optimized Loading)
// ============================================================================

function MapSectionVariation({ id, ...contentProps }: MapSectionProps) {
  const { t } = useTranslation(Namespaces.LISTING);

  return (
    <ContentSectionTemplate id={id} heading={t(LOCALE_LISTING_PATHS.TAB_MAP)}>
      <MapContent {...contentProps} useOptimizedLoading />
    </ContentSectionTemplate>
  );
}

// ============================================================================
// Switch
// ============================================================================

export function MapSectionSwitch(props: MapSectionProps) {
  return (
    <ExperimentalSection
      experimentKey={AllowedExperimentKey.STICKY_HEADER_TABS}
      control={<MapSectionControl {...props} />}
      variation={<MapSectionVariation {...props} />}
    />
  );
}
